import { useState, useEffect, Fragment } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import H2 from 'UIComponents/elements/headings/H2';
import * as RequestStatus from 'ContentUtils/constants/RequestStatus';
import * as formsSelectors from 'ContentData/selectors/forms';
import { fetchWorkflowReferences } from 'ContentData/api/Workflows';
import * as FormsActions from 'ContentData/actions/Forms';
import WorkflowLink from 'ContentComponents/cmv2/form/formAutomation/connectedWorkflows/WorkflowLink';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.form.formWorkflowDependencies.${key}`;
function mapStateToProps(state, props) {
  const {
    formWorkflowDependency,
    formGuid
  } = props;
  const {
    flowId
  } = formWorkflowDependency || {};
  return {
    dependencyDeleteRequestStatus: formsSelectors.getFormWorkflowDependencyDeleteRequestStatus(state, {
      flowId,
      formGuid
    })
  };
}
const mapDispatchToProps = {
  deleteFormWorkflowDependency: FormsActions.deleteFormWorkflowDependency,
  deleteFormWorkflowDependencyAndWorkflow: FormsActions.deleteFormWorkflowDependencyAndWorkflow
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const RemovalModal = ({
  isRemoving,
  modalI18nKey,
  onClose,
  onRemoveWorkflow,
  workflowLink
}) => {
  return /*#__PURE__*/_jsxs(UIModal, {
    use: "danger",
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`${modalI18nKey}.header`)
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey(`${modalI18nKey}.message`),
          options: {
            workflowLink
          }
        })
      })
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UILoadingButton, {
        loading: isRemoving,
        onClick: onRemoveWorkflow,
        use: "danger",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`${modalI18nKey}.confirm`)
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: onClose,
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`${modalI18nKey}.cancel`)
        })
      })]
    })]
  });
};
const HasReferencesModal = ({
  onClose,
  workflowLink,
  workflowName
}) => {
  return /*#__PURE__*/_jsxs(UIModal, {
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hasReferencesModal.header')
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('hasReferencesModal.message'),
          options: {
            workflowName,
            workflowLink
          }
        })
      })
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      children: /*#__PURE__*/_jsx(UIButton, {
        onClick: onClose,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hasReferencesModal.close')
        })
      })
    })]
  });
};
const RemoveFormWorkflowDependencyButton = ({
  contentId,
  deleteFormWorkflowDependency,
  deleteFormWorkflowDependencyAndWorkflow,
  formGuid,
  formWorkflowDependency,
  hasWorkflowsAppAccess,
  hasWorkflowsDeleteAccess,
  onRemoveWorkflowEvent,
  dependencyDeleteRequestStatus
}) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isHasReferencesModalOpen, setIsHasReferencesModalOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const {
    canDelete,
    canRemoveEnrollmentCriteria,
    flowId,
    name
  } = formWorkflowDependency;
  const prevDependencyDeleteRequestStatus = usePrevious(dependencyDeleteRequestStatus);
  useEffect(() => {
    if (isRemoving && dependencyDeleteRequestStatus !== prevDependencyDeleteRequestStatus) {
      if (dependencyDeleteRequestStatus === RequestStatus.FAILED) {
        let deleteErrorI18nKey = 'removeFormFromWorkflowModal.removeError';
        if (canDelete) {
          deleteErrorI18nKey = 'deleteWorkflowModal.deleteError';
        }
        FloatingAlertStore.addAlert({
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(deleteErrorI18nKey),
            options: {
              workflowName: name
            }
          }),
          type: 'danger'
        });
      }
      if (dependencyDeleteRequestStatus === RequestStatus.FAILED || dependencyDeleteRequestStatus === RequestStatus.SUCCEEDED) {
        setIsRemoving(false);
        setIsRemoveModalOpen(false);
      }
    }
  }, [dependencyDeleteRequestStatus, prevDependencyDeleteRequestStatus, isRemoving, name, canDelete]);
  const closeRemoveModal = () => {
    setIsRemoveModalOpen(false);
  };
  const closeHasReferencesModal = () => {
    setIsHasReferencesModalOpen(false);
  };
  const onRemoveWorkflowClick = () => {
    if (canDelete) {
      fetchWorkflowReferences(flowId).then(response => {
        const {
          flowReferences
        } = response || {};
        if (flowReferences && flowReferences.length) {
          setIsHasReferencesModalOpen(true);
        } else {
          setIsRemoveModalOpen(true);
        }
      }).catch(() => {
        FloatingAlertStore.addAlert({
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('deleteWorkflowModal.deleteError'),
            options: {
              workflowName: name
            }
          }),
          type: 'danger'
        });
      });
    } else {
      setIsRemoveModalOpen(true);
    }
  };
  let onRemoveWorkflow = () => {
    setIsRemoving(true);
    if (onRemoveWorkflowEvent) onRemoveWorkflowEvent();
    deleteFormWorkflowDependency(flowId, formGuid, contentId).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  };
  let removeModalI18nKey = 'removeFormFromWorkflowModal';
  let removeButtonTooltipI18nKey = 'dependencyRow.removeTooltip';
  let removeButtonIconName = 'remove';
  let removeIsDisabled = false;
  if (canDelete) {
    onRemoveWorkflow = () => {
      setIsRemoving(true);
      if (onRemoveWorkflowEvent) onRemoveWorkflowEvent();
      deleteFormWorkflowDependencyAndWorkflow(flowId, formGuid).catch(err => {
        setTimeout(() => {
          throw err;
        });
      });
    };
    removeModalI18nKey = 'deleteWorkflowModal';
    removeButtonTooltipI18nKey = 'dependencyRow.deleteTooltip';
    removeButtonIconName = 'delete';
    if (!hasWorkflowsDeleteAccess) {
      removeButtonTooltipI18nKey = 'dependencyRow.disabledDeleteTooltip';
      removeIsDisabled = true;
    }
  } else if (!canRemoveEnrollmentCriteria) {
    removeButtonTooltipI18nKey = 'dependencyRow.disabledRemoveTooltip';
    removeIsDisabled = true;
  }
  const workflowLink = /*#__PURE__*/_jsx(WorkflowLink, {
    formGuid: formGuid,
    formWorkflowDependency: formWorkflowDependency,
    hasWorkflowsAppAccess: hasWorkflowsAppAccess
  });
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [isRemoveModalOpen && /*#__PURE__*/_jsx(RemovalModal, {
      isRemoving: isRemoving,
      modalI18nKey: removeModalI18nKey,
      onClose: closeRemoveModal,
      onRemoveWorkflow: onRemoveWorkflow,
      workflowLink: workflowLink
    }), isHasReferencesModalOpen && /*#__PURE__*/_jsx(HasReferencesModal, {
      onClose: closeHasReferencesModal,
      workflowLink: workflowLink,
      workflowName: name
    }), /*#__PURE__*/_jsx(UIIconButton, {
      disabled: removeIsDisabled,
      onClick: onRemoveWorkflowClick,
      size: "xs",
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(removeButtonTooltipI18nKey)
      }),
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: removeButtonIconName
      })
    })]
  });
};
export default connector(RemoveFormWorkflowDependencyButton);